import ReactDOM from 'react-dom';
import { useIframeContext } from '../../context/IframeContext';
import { useContext, useEffect, useState } from 'react';
import LayersContext from '../../context/LayersContext';
import { formatValueBase } from '../../utils/helpers';
import ItemCard from './ItemCard';

interface LayersModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const LayersModal: React.FC<LayersModalProps> = ({ isOpen, onClose }) => {
    const { isIframe } = useIframeContext();
    const { layers, items } = useContext(LayersContext);
    const [activeCardId, setActiveCardId] = useState<number | null>(null); // Track active card
    const [activeLayer, setActiveLayer] = useState<string | null>(null);
    const [selectedCategory, setSelectedCategory] = useState<string>('');
    const [currentPage, setCurrentPage] = useState<number>(1); 
    const itemsPerPage = 10;

    // Set the active layer id
    const activeLayerId = layers.find(layer => layer.layer_name.toLowerCase() === activeLayer?.toLowerCase())?.layer_id || null;

    // Grab summary information from layers context
    // const summary = items?.summary[0];
    const layerItems = items?.items;

    // Filter layer items based on the selected category
    const filteredLayerItems = selectedCategory
        ? layerItems?.filter(item => item.category === selectedCategory)
        : layerItems;

    // Calculate the total value of the filtered items
    const totalValue = filteredLayerItems?.reduce((sum, item) => sum + (item.value || 0), 0) || 0;

    // Paginate the filtered items
    const totalItems = filteredLayerItems?.length || 0;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedItems = filteredLayerItems?.slice(startIndex, startIndex + itemsPerPage);

    // Hide main scrollbar when the metadata panel is open
    useEffect(() => {
        document.body.style.overflow = isOpen ? 'hidden' : '';
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    if (!isOpen) return null;

    // Get unique categories from layerItems for the dropdown
    const categories = Array.from(new Set(layerItems?.map(item => item.category) || []));

    // Handle page change
    const handlePageChange = (page: number) => {
        if (page < 1 || page > totalPages) return; // Prevent invalid page numbers
        setCurrentPage(page);
    };

    return ReactDOM.createPortal(
        <div className={`fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50 transition-all duration-500 transform ${!isOpen ? 'translate-y-full' : 'translate-y-0'} ${!isIframe && 'max-w-[456px] mx-auto'}`}>
            <div className={`bg-white p-6 w-full overflow-y-auto h-full relative transition-transform duration-500 transform ${!isOpen ? 'translate-y-full' : 'translate-y-0'}`}>
                <button
                    className="absolute top-3 right-5 text-gray-600 text-3xl font-bold hover:text-black"
                    onClick={onClose}
                    aria-label="Close modal"
                >
                    &times;
                </button>
                {/* Layer Buttons */}
                <div className="flex flex-wrap items-center justify-center gap-3 mb-4">
                    {layers.map((layer) => (
                        <button
                            key={layer.layer_id}
                            className={`px-4 py-2 text-sm rounded-md ${
                                activeLayer === layer.layer_name
                                    ? 'bg-color-cai-dashboard text-white'
                                    : 'bg-white text-color-cai-dashboard border'
                            }`}
                            onClick={() => setActiveLayer(layer.layer_name)}
                        >
                            {layer.layer_name}
                        </button>
                    ))}
                </div>

                {/* Message when no active layer is selected */}
                {!activeLayer && (
                    <div className="mt-4 p-4 text-center text-gray-600">
                        <p>Please select a layer above to view its details.</p>
                    </div>
                )}

                {/* Active Layer Content for SKCF */}
                {activeLayer === 'South King County Fund' && (
                    <>
                        {/* Category Dropdown */}
                        <div className="mt-4">
                            <label htmlFor="category" className="block text-sm font-medium text-gray-700">
                                Filter by Category
                            </label>
                            <select
                                id="category"
                                value={selectedCategory}
                                onChange={(e) => setSelectedCategory(e.target.value)}
                                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                            >
                                <option value="">All Categories</option>
                                {categories.map((category, index) => (
                                    <option key={index} value={category}>
                                        {category}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Totals */}
                        <div className="mt-4 p-4 text-color-cai-gray flex items-center justify-center gap-4">
                            <p className="">
                                <strong>Item Count:</strong> {formatValueBase(totalItems, 'integer')}
                            </p>
                            <p className='text-lg font-thin text-gray-500'>|</p>
                            <p className="">
                                <strong>Total:</strong> {formatValueBase(totalValue, 'currency')}
                            </p>
                        </div>

                        {/* Item Cards */}
                        <div className="grid grid-cols-1 gap-4 mt-4">
                            {paginatedItems?.map((layerItem, index) => (
                                <ItemCard 
                                    key={index} 
                                    width="w-full" 
                                    layerItem={layerItem} 
                                    layerId={activeLayerId}
                                    isActive={activeCardId === layerItem.id}
                                    setActiveCardId={setActiveCardId} 
                                />
                            ))}
                        </div>

                        {/* Pagination Controls */}
                        <div className="mt-4 flex justify-center gap-4 text-xs items-center">
                            <button
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md disabled:opacity-50 w-24"
                            >
                                Previous
                            </button>
                            
                            <span className="text-gray-700">
                                Page {currentPage} of {totalPages}
                            </span>
                            
                            <button
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md disabled:opacity-50 w-24"
                            >
                                Next
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>,
        document.body
    );
};

export default LayersModal;